import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import Loader from '../../components/ui/Loader';
import { useAuth } from '../../components/shared/AuthContext';
import { PageLayout, Table, Button } from "../../components/ui";
import ReportHeader from "../../components/reports/ReportHeader";

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const [day, month, year] = dateString.split('-').map(Number);
  return `${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}-${year}`;
};

const getDaysInMonth = (year, month) => {
  return new Date(year, month, 0).getDate();
};

const getCurrentMonthDates = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const daysInMonth = getDaysInMonth(year, month);

  const dates = [];
  for (let day = 1; day <= daysInMonth; day++) {
    dates.push({
      day,
      dateString: `${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}-${year}`,
      displayDate: `${String(day).padStart(2, '0')}`
    });
  }
  return { dates, year, month, monthName: new Date(year, month - 1).toLocaleString('default', { month: 'long' }) };
};

const fetchAllData = async (userBranchId = null, isSuperAdmin = false, selectedBranchId = null, selectedMonth = null) => {
  try {
    const filterQuery = {};

    if (isSuperAdmin && selectedBranchId) {
      filterQuery.branchId = parseInt(selectedBranchId);
    } else if (!isSuperAdmin && userBranchId) {
      filterQuery.branchId = userBranchId;
    }

    if (selectedMonth) {
      filterQuery.month = selectedMonth;
    }

    const response = await axios.get(`${API_BASE_URL}/api/getsavings_follow_up_report`, {
      params: filterQuery,
      timeout: 30000,
    });

    if (!response.data || !response.data.data || !Array.isArray(response.data.data.customers)) {
      console.error('Invalid response structure:', response.data);
      return { customers: [], branches: [] };
    }

    return {
      customers: response.data.data.customers,
      branches: response.data.data.branches || []
    };

  } catch (error) {
    console.error('Error fetching data:', error);
    throw error;
  }
};

const SFU_report = () => {
  const { roleName, data } = useAuth();
  const isSuperAdmin = roleName === 'superadmin';
  const userBranchId = data?.branchId;
  const shouldFilterByBranch = !isSuperAdmin && userBranchId;

  const [rawData, setRawData] = useState(null);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [transactionType, setTransactionType] = useState('all');
  const [loading, setLoading] = useState(false);
  const [branchList, setBranchList] = useState([]);
  const [error, setError] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');

  // Fetch branches for super admin
  useEffect(() => {
    if (isSuperAdmin) {
      const fetchBranches = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/branches`, {
            timeout: 5000,
          });

          if (response.data && Array.isArray(response.data)) {
            setBranchList(response.data);
          } else if (response.data && Array.isArray(response.data.data)) {
            setBranchList(response.data.data);
          }
        } catch (error) {
          console.error('Error fetching branches:', error);
          setError('Failed to load branches');
        }
      };
      fetchBranches();
    }
  }, [isSuperAdmin]);

  // Fetch data when filters change
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchAllData(
          shouldFilterByBranch ? userBranchId : null,
          isSuperAdmin,
          isSuperAdmin ? selectedBranchId : null,
          selectedMonth
        );

        setRawData(data);
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [shouldFilterByBranch, userBranchId, isSuperAdmin, selectedBranchId, selectedMonth]);

  // Process data based on current filters
  const processedData = useMemo(() => {
    if (!rawData || !rawData.customers) {
      return { customers: [], monthDates: getCurrentMonthDates() };
    }

    const [year, month] = selectedMonth.split('-').map(Number);
    const monthDates = {
      dates: [],
      year,
      month,
      monthName: new Date(year, month - 1).toLocaleString('default', { month: 'long' })
    };

    const daysInMonth = getDaysInMonth(year, month);
    for (let day = 1; day <= daysInMonth; day++) {
      monthDates.dates.push({
        day,
        dateString: `${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}-${year}`,
        displayDate: `${String(day).padStart(2, '0')}`
      });
    }

    // Process customers and their transactions for the selected month
    const processedCustomers = rawData.customers.map(customer => {
      const customerData = {
        customerUniqueId: customer.customerUniqueId,
        customerName: customer.customerName,
        branchId: customer.branchId,
        dailyData: {},
        monthlyTotals: {
          totalDeposits: 0,
          totalWithdrawals: 0,
          totalTransactions: 0,
          accountBalance: 0
        }
      };

      // Initialize daily data
      monthDates.dates.forEach(date => {
        customerData.dailyData[date.dateString] = {
          deposits: 0,
          withdrawals: 0,
          transactions: [],
          netAmount: 0
        };
      });

      // Process monthly transactions
      if (customer.monthlyTransactions && Array.isArray(customer.monthlyTransactions)) {
        customer.monthlyTransactions.forEach(monthData => {
          if (monthData.dates && Array.isArray(monthData.dates)) {
            monthData.dates.forEach(dateData => {
              const dateKey = dateData.date;

              if (customerData.dailyData[dateKey]) {
                let dayDeposits = 0;
                let dayWithdrawals = 0;
                const dayTransactions = [];

                if (dateData.transactions && Array.isArray(dateData.transactions)) {
                  dateData.transactions.forEach(tx => {
                    if (transactionType === 'all' || tx.transactionType === transactionType) {
                      const amount = parseFloat(tx.amount) || 0;

                      if (tx.transactionType === 'deposit') {
                        dayDeposits += amount;
                        customerData.monthlyTotals.totalDeposits += amount;
                      } else if (tx.transactionType === 'withdraw') {
                        dayWithdrawals += amount;
                        customerData.monthlyTotals.totalWithdrawals += amount;
                      }

                      dayTransactions.push({
                        ...tx,
                        amount: amount.toFixed(2)
                      });
                      customerData.monthlyTotals.totalTransactions++;
                    }
                  });
                }

                customerData.dailyData[dateKey] = {
                  deposits: dayDeposits,
                  withdrawals: dayWithdrawals,
                  transactions: dayTransactions,
                  netAmount: dayDeposits - dayWithdrawals
                };
              }
            });
          }
        });
      }

      customerData.monthlyTotals.accountBalance =
        customerData.monthlyTotals.totalDeposits - customerData.monthlyTotals.totalWithdrawals;

      return customerData;
    });

    let customersWithData = processedCustomers.filter(customer =>
      customer.monthlyTotals.totalTransactions > 0
    );

    if (customerSearch.trim()) {
      const searchLower = customerSearch.trim().toLowerCase();
      customersWithData = customersWithData.filter(customer =>
        customer.customerName.toLowerCase().includes(searchLower)
      );
    }

    return {
      customers: customersWithData,
      monthDates
    };
  }, [rawData, selectedMonth, transactionType, customerSearch]);

  const handleBranchChange = (e) => {
    setSelectedBranchId(e.target.value);
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
  };

  const handleTransactionTypeChange = (e) => {
    setTransactionType(e.target.value);
  };

  const exportToExcel = () => {
    if (!processedData.customers.length) {
      alert('No data to export');
      return;
    }

    const { customers, monthDates } = processedData;

    const headerRow = ['Customer Name', ...monthDates.dates.map(d => d.displayDate), 'Total Deposits', 'Total Withdrawals', 'Net Amount'];

    const dataRows = customers.map(customer => {
      const row = [customer.customerName];

      monthDates.dates.forEach(date => {
        const dayData = customer.dailyData[date.dateString];
        const netAmount = dayData.netAmount;
        row.push(netAmount !== 0 ? netAmount.toFixed(2) : '');
      });

      row.push(
        customer.monthlyTotals.totalDeposits.toFixed(2),
        customer.monthlyTotals.totalWithdrawals.toFixed(2),
        customer.monthlyTotals.accountBalance.toFixed(2)
      );

      return row;
    });

    const worksheetData = [headerRow, ...dataRows];

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let c = range.s.c; c <= range.e.c; c++) {
      const headerCell = XLSX.utils.encode_cell({ r: 0, c });
      if (worksheet[headerCell]) {
        worksheet[headerCell].s = { font: { bold: true } };
      }
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Monthly Report');
    XLSX.writeFile(workbook, `Monthly_Report_${selectedMonth}.xlsx`);
  };

  return (
    <PageLayout className="h-auto min-h-[calc(100vh-120px)] overflow-y-auto pb-4">
      {/* Top Filter and Actions Bar using reusable ReportHeader */}
      <ReportHeader
        filters={
          <>
            {/* Month Picker */}
            <div className="relative min-w-[150px] mt-1">
              <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Month
              </label>
              <input
                type="month"
                value={selectedMonth}
                onChange={handleMonthChange}
                className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs px-3.5 w-full focus:outline-none transition-all font-medium cursor-pointer"
              />
            </div>

            {/* Branch Filter */}
            {isSuperAdmin && (
              <div className="relative min-w-[160px] mt-1">
                <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                  Branch
                </label>
                <div className="relative">
                  <select
                    value={selectedBranchId}
                    onChange={handleBranchChange}
                    className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                  >
                    <option value="">All Branches</option>
                    {branchList.map(branch => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name || `Branch ${branch.id}`}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            )}

            {/* Type Filter */}
            <div className="relative min-w-[130px] mt-1">
              <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Type
              </label>
              <div className="relative">
                <select
                  value={transactionType}
                  onChange={handleTransactionTypeChange}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                >
                  <option value="all">All</option>
                  <option value="deposit">Deposit</option>
                  <option value="withdraw">Withdraw</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Search Input */}
            <div className="min-w-[220px] mt-1">
              <input
                placeholder="Search by customer name..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                className="w-full bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary text-ink-700 h-[38px] text-xs px-3.5 focus:outline-none transition-all font-medium"
              />
            </div>
          </>
        }
        actions={
          <Button
            onClick={exportToExcel}
            disabled={!processedData.customers.length}
            variant={processedData.customers.length === 0 ? "secondary" : "primary"}
            size="sm"
            className="shadow-sm h-[38px] px-4 font-bold"
          >
            <i className="fa-solid fa-file-excel mr-1.5"></i>
            Export to Excel
          </Button>
        }
      />
      {/* Summary Cards */}
      {processedData.customers.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-[#E9DFFF] border border-[#7C3AED]/25 py-2.5 px-3.5 rounded-xl shadow-xs flex flex-col justify-center">
            <span className="text-[10px] font-bold text-[#7C3AED] uppercase tracking-wider mb-0.5">Total Customers</span>
            <span className="text-base font-black text-[#7C3AED]">{processedData.customers.length}</span>
          </div>
          <div className="bg-[#DDF5E6] border border-[#16A34A]/25 py-2.5 px-3.5 rounded-xl shadow-xs flex flex-col justify-center">
            <span className="text-[10px] font-bold text-[#16A34A] uppercase tracking-wider mb-0.5">Total Deposits</span>
            <span className="text-base font-black text-[#16A34A]">
              ₹{processedData.customers.reduce((sum, c) => sum + c.monthlyTotals.totalDeposits, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="bg-[#FFF0C7] border border-[#D97706]/25 py-2.5 px-3.5 rounded-xl shadow-xs flex flex-col justify-center">
            <span className="text-[10px] font-bold text-[#D97706] uppercase tracking-wider mb-0.5">Total Withdrawals</span>
            <span className="text-base font-black text-[#D97706]">
              ₹{processedData.customers.reduce((sum, c) => sum + c.monthlyTotals.totalWithdrawals, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="bg-[#D9F3F7] border border-[#0891B2]/25 py-2.5 px-3.5 rounded-xl shadow-xs flex flex-col justify-center">
            <span className="text-[10px] font-bold text-[#0891B2] uppercase tracking-wider mb-0.5">Net Amount</span>
            <span className="text-base font-black text-[#0891B2]">
              ₹{processedData.customers.reduce((sum, c) => sum + c.monthlyTotals.accountBalance, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      )}
      {loading && !rawData && <Loader message="Loading transaction data..." />}

      {error && (
        <div className="bg-rose-50 text-rose-700 p-4 rounded-xl border border-rose-200 text-sm font-medium mb-4">
          {error}
        </div>
      )}

      <div className="mb-4">
        <h5 className="text-sm font-bold text-ink-800">
          {processedData.monthDates.monthName} {processedData.monthDates.year} — {processedData.customers.length} Customer(s) with transactions
        </h5>
      </div>

      {/* Monthly Calendar Table */}
      <div className="flex flex-col">
        <div className="overflow-x-auto border border-border-light rounded-2xl shadow-sm bg-white min-h-[440px]">
          <table className="w-full text-xs text-ink-700 border-collapse min-w-[1200px]">
            <thead>
              <tr className="bg-canvas-muted border-b border-border-light h-12">
                <th className="p-3 text-left font-bold uppercase tracking-wider text-ink-500 sticky left-0 bg-slate-100 z-20 w-[180px] min-w-[180px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.08)]">
                  Customer Name
                </th>
                {processedData.monthDates.dates.map(date => (
                  <th key={date.dateString} className="p-1 text-center font-bold text-ink-500 w-8 border-l border-border-light/40">
                    {date.displayDate}
                  </th>
                ))}
                <th className="p-3 text-center font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50/50 w-28 border-l border-border-light">
                  Total Deposits
                </th>
                <th className="p-3 text-center font-bold uppercase tracking-wider text-rose-700 bg-rose-50/50 w-28 border-l border-border-light">
                  Total Withdrawals
                </th>
                <th className="p-3 text-center font-bold uppercase tracking-wider text-primary bg-primary/5 w-28 border-l border-border-light">
                  Net Amount
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-light">
              {loading ? (
                <tr>
                  <td colSpan={processedData.monthDates.dates.length + 4} className="text-center py-28 text-ink-500 font-medium">
                    Loading transactions...
                  </td>
                </tr>
              ) : processedData.customers.length > 0 ? (
                processedData.customers.map((customer, customerIndex) => {
                  const isOdd = customerIndex % 2 !== 0;
                  const rowBg = isOdd ? "bg-[#f8fafc]" : "bg-white";
                  return (
                    <tr key={customer.customerUniqueId} className={`${rowBg} hover:bg-slate-100 transition-colors h-14`}>
                      <td className={`p-3 sticky left-0 font-semibold text-ink-900 border-r border-border-light/60 ${rowBg} z-10 w-[180px] min-w-[180px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.08)]`}>
                        <div className="truncate max-w-[160px]" title={customer.customerName}>{customer.customerName}</div>
                        <div className="text-[10px] text-ink-400 font-mono font-normal">ID: {customer.customerUniqueId}</div>
                      </td>
                      {processedData.monthDates.dates.map(date => {
                        const dayData = customer.dailyData[date.dateString];
                        const hasTransactions = dayData.transactions.length > 0;
                        const netAmount = dayData.netAmount;

                        let cellClass = "text-ink-300";
                        let cellBg = "";
                        if (hasTransactions) {
                          if (netAmount > 0) {
                            cellClass = "text-emerald-700 font-semibold";
                            cellBg = "bg-emerald-50/40";
                          } else if (netAmount < 0) {
                            cellClass = "text-rose-700 font-semibold";
                            cellBg = "bg-rose-50/40";
                          } else {
                            cellClass = "text-amber-700 font-semibold";
                            cellBg = "bg-amber-50/45";
                          }
                        }

                        return (
                          <td
                            key={date.dateString}
                            className={`p-1 text-center border-l border-border-light/40 text-[10px] ${cellClass} ${cellBg}`}
                            title={hasTransactions ? `Transactions: ${dayData.transactions.length}` : ""}
                          >
                            {hasTransactions ? (
                              <div>
                                {netAmount > 0 ? `+${netAmount.toFixed(0)}` :
                                  netAmount < 0 ? netAmount.toFixed(0) : '0'}
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                        );
                      })}
                      <td className="p-3 text-center font-bold text-emerald-600 bg-emerald-50/10 border-l border-border-light">
                        ₹{customer.monthlyTotals.totalDeposits.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </td>
                      <td className="p-3 text-center font-bold text-rose-600 bg-rose-50/10 border-l border-border-light">
                        ₹{customer.monthlyTotals.totalWithdrawals.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </td>
                      <td className={`p-3 text-center font-black border-l border-border-light ${customer.monthlyTotals.accountBalance >= 0 ? 'text-emerald-600 bg-emerald-50/5' : 'text-rose-600 bg-rose-50/5'
                        }`}>
                        ₹{customer.monthlyTotals.accountBalance.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={processedData.monthDates.dates.length + 4} className="text-center py-28 text-ink-400 font-medium">
                    No transactions found for the selected criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </PageLayout>
  );
};

export default SFU_report;